import { AdvancedList, Variable } from "@amzn/altar-sds-client";
import {
    Action,
    Condition,
    FieldConfiguration,
    Workflow
} from "@amzn/ask-legal-domain";
import {
    FormField,
    FormFieldProps,
    Header,
    Input,
    SpaceBetween
} from "@amzn/awsui-components-react";
import * as React from "react";
import { EmailNotification } from "./EmailNotification";
import { buildBodyWithItemLink, buildStaticEmailAction, extractEmailIdsFromAddresses, stripItemLink } from "./utils/EmailWorkflowUtils";

export const ItemCreate = (props: {
    advancedList: AdvancedList;
    fieldConfigurations?: FieldConfiguration.Record;
    variables: Variable[];
    containerId?: string;
    value?: Workflow.Payload;
    onUpdated?: (value: Workflow.Payload) => void;
}) => {
    const [formFieldWorkflowName, setFormFieldWorkflowName] = React.useState<FormFieldProps>({});
    const [workflowName, setWorkflowName] = React.useState<string>(props.value?.name ?? "");

    const action = props.value?.action;
    const isPersonFieldAction = props.value?.actionType === Action.Type.EmailToPersonFieldValue;
    const [emailNotification, setEmailNotification] = React.useState<EmailNotification>({
        usePersonField: isPersonFieldAction,
        personFieldKey: isPersonFieldAction ? (action as Action.EmailToPersonFieldValue).personFieldKey : "",
        emailSubject: action?.subject || "",
        emailBody: stripItemLink(action?.body || ""),
        emailAddressList: !isPersonFieldAction &&
            Array.isArray((action as Action.EmailToStaticRecipients)?.emailAddressesList)
            ? extractEmailIdsFromAddresses(
                (action as Action.EmailToStaticRecipients).emailAddressesList
            )
            : [],
    });

    React.useEffect(() => {
        let valid = true;
        if (!workflowName?.trim().length) {
            setFormFieldWorkflowName({
                errorText: "Workflow Name is required"
            });
            valid = false;
        } else {
            setFormFieldWorkflowName({
                errorText: ""
            });
        }
        const usePersonField = emailNotification.usePersonField ?? true;
        const hasSubject = emailNotification?.emailSubject?.trim().length;
        const hasBody = emailNotification?.emailBody?.trim().length;
        const hasPerson = emailNotification?.personFieldKey?.trim().length;
        const hasAliases = emailNotification?.emailAddressList?.length;
        if (
            !hasSubject ||
            !hasBody ||
            (usePersonField && !hasPerson) ||
            (!usePersonField && !hasAliases)
        ) {
            valid = false;
        }
        if (!valid) {
            console.warn(`not a valid data`, workflowName, emailNotification);
            return;
        }
        let action: Action.EmailToPersonFieldValue | Action.EmailToStaticRecipients;
        let actionType: Action.Type;
        const emailBodyWithLink = buildBodyWithItemLink(
            emailNotification.emailBody,
            props.containerId
        );
        if (usePersonField) {
            action = new Action.EmailToPersonFieldValue(
                emailNotification.emailSubject.trim(),
                emailBodyWithLink.trim(),
                emailNotification.personFieldKey.trim()
            );
            actionType = Action.Type.EmailToPersonFieldValue;
        } else {
            const result = buildStaticEmailAction(
                emailNotification.emailSubject,
                emailBodyWithLink,
                emailNotification.emailAddressList
            );
            action = result.action;
            actionType = result.actionType;
        }
        const workflowPayload = Workflow.Payload.init({
            id: props.value?.id,
            name: workflowName.trim(),
            conditionType: Condition.Type.ItemCreated,
            condition: props.value?.condition,
            action,
            actionType,
            ref: props.value?.ref,
            variables: props.value?.variables ?? props.variables,
            disabled: props.value?.disabled
        });
        props.onUpdated?.(workflowPayload);
    }, [workflowName, emailNotification]);
    return (
        <SpaceBetween size={"s"}>
            <SpaceBetween direction="vertical" size="xxs">
                <Header headingTagOverride="h4">Workflow Name</Header>
                <FormField errorText={formFieldWorkflowName.errorText}>
                    <Input value={workflowName} onChange={(e) => setWorkflowName(e.detail.value)} />
                </FormField>
            </SpaceBetween>

            <EmailNotification
                fieldDefinitions={props.advancedList.fieldDefinitions ?? []}
                emailNotification={emailNotification}
                fieldConfigurations={props.fieldConfigurations}
                workflowNotificationType={Condition.Type.ItemCreated}
                containerId={props.containerId}
                onChanged={(value: EmailNotification) => {
                    setEmailNotification(value);
                }}
                variables={props.value?.variables || props.variables}
            />
        </SpaceBetween>
    );
};
